import { ConfirmationDialog } from '@/components/ConfirmationDialog';

/**
 * BulkConfirmationDialog — Safety gate before destructive bulk actions.
 *
 * Required for ALL bulk endpoint calls (>20 manual items OR 'all' mode).
 * Per-item path (≤20) does NOT require confirmation (existing behavior).
 *
 * Shows:
 * - Action name
 * - "all matching" or item count
 * - Resource type
 * - "This action will be processed in the background and cannot be undone."
 * - [Cancel] [Confirm] buttons
 *
 * @example
 * ```tsx
 * <BulkConfirmationDialog
 *   isOpen={showConfirm}
 *   actionName="freeze"
 *   resourceLabel="assets"
 *   isSelectAll={selectionState.mode === 'all'}
 *   itemCount={selectedRows.length}
 *   onCancel={() => setShowConfirm(false)}
 *   onConfirm={() => { submitBulk(payload); setShowConfirm(false); }}
 *   isPending={isPending}
 * />
 * ```
 */
export interface BulkConfirmationDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Human-readable action name (e.g. "freeze", "delete", "export") */
  actionName: string;
  /** Resource label in plural (e.g. "assets", "vulnerabilities") */
  resourceLabel: string;
  /** Whether selection is in 'all' mode (shows "all matching" instead of count) */
  isSelectAll?: boolean;
  /** Number of items to act on (used when isSelectAll is false) */
  itemCount?: number;
  /** Called when user clicks Cancel */
  onCancel: () => void;
  /** Called when user clicks Confirm */
  onConfirm: () => void;
  /** Whether the bulk action is currently submitting */
  isPending?: boolean;
  /** Test identifier for the dialog root element */
  'data-testid'?: string;
}

/**
 * BulkConfirmationDialog — Confirmation dialog for bulk actions.
 */
export function BulkConfirmationDialog({
  isOpen,
  actionName,
  resourceLabel,
  isSelectAll = false,
  itemCount,
  onCancel,
  onConfirm,
  isPending = false,
  'data-testid': dataTestId = 'bulk-confirmation-dialog',
}: BulkConfirmationDialogProps) {
  const selectionDescription = isSelectAll
    ? `all matching ${resourceLabel}`
    : `${itemCount?.toLocaleString() ?? 'selected'} ${resourceLabel}`;

  const description = `You are about to ${actionName} ${selectionDescription}. This action will be processed in the background and cannot be undone.`;

  return (
    <ConfirmationDialog
      open={isOpen}
      onClose={onCancel}
      onConfirm={onConfirm}
      title={`Confirm bulk ${actionName}`}
      description={description}
      confirmLabel="Confirm"
      variant="destructive"
      isLoading={isPending}
      loadingLabel="Submitting..."
      data-testid={dataTestId}
    />
  );
}
